import {office,type ContentFile} from '../../../../packages/content-client/src'
import {saveToForge} from '../durableSave'
import {createEditorSession} from './EditorSession'
import type {EditorAdapter,EditorCapabilities,EditorCommand,EditorSelection,InlineEvent} from './EditorPort'

const commands:Record<EditorCommand,string>={bold:'.uno:Bold',italic:'.uno:Italic',underline:'.uno:Underline',undo:'.uno:Undo',redo:'.uno:Redo'}
interface Message {MessageId?:string;Values?:Record<string,unknown>}
interface Options {file:ContentFile;url:string;frame:()=>HTMLIFrameElement|null;status:(message:string)=>void;context:()=>void;indexed:()=>void}

/** All native protocol/version assumptions stay in this adapter. */
export function createCollaboraAdapter(options:Options):EditorAdapter {
 const origin=new URL(options.url).origin,session=crypto.randomUUID()
 let stopped=false,loaded=false,poisoned=false,sequence=0,capabilities:EditorCapabilities|null=null
 const listeners=new Set<()=>void>(),inlineListeners=new Set<(event:InlineEvent)=>void>()
 type Pending={name:string;id:string;resolve:(values:Record<string,unknown>)=>void;reject:(error:Error)=>void;timer:ReturnType<typeof setTimeout>}
 let pending:Pending|null=null
 const emit=()=>listeners.forEach(listener=>listener())
 function send(message:Message,binary=false){if(!stopped)options.frame()?.contentWindow?.postMessage(binary?message:JSON.stringify(message),origin)}
 const bridge=(name:string,values:Record<string,unknown>={})=>send({MessageId:name,Values:{...values,session}})
 const ensureSelection=()=>{if(!capabilities?.selection)throw Error('Selection assistance needs a compatible editor bridge. File editing and saving remain available.')}
 function request(name:string,id:string,action:()=>void,timeout=10000){
  if(stopped)throw Error('The editor was closed.')
  if(pending||poisoned)throw Error('Another editor request is unfinished. Reopen the file after a selection timeout.')
  return new Promise<Record<string,unknown>>((resolve,reject)=>{
   pending={name,id,resolve,reject,timer:setTimeout(()=>{pending=null;if(name==='Action_Copy_Resp')poisoned=true;emit();reject(Error('The editor did not confirm the request. Reopen the file after a selection timeout.'))},timeout)}
   try{action()}catch(error){clearTimeout(pending.timer);pending=null;reject(error)}
  })
 }
 async function copyText(){
  ensureSelection()
  const response=await request('Action_Copy_Resp','copy',()=>send({MessageId:'Action_Copy',Values:{Mimetype:'text/plain;charset=utf-8'}}),5000)
  if(typeof response.content!=='string'||response.content.length>4000)throw Error('Select up to 4000 characters and retry.')
  return response.content
 }
 function hello(){bridge('Folio_Bridge_Hello',{protocol:1,file_id:options.file.file_id})}
 function receive(event:MessageEvent){
  if(stopped||event.origin!==origin||event.source!==options.frame()?.contentWindow)return
  let data:Message
  try{data=typeof event.data==='string'?JSON.parse(event.data):event.data}catch{return}
  if(!data||typeof data!=='object')return
  const values=data.Values||{},name=data.MessageId
  if(name==='App_LoadingStatus'&&values.Status==='Document_Loaded'){
   loaded=true;send({MessageId:'Host_PostmessageReady'});hello()
   send({MessageId:'Insert_Button',Values:{id:'folio-contextful',label:'Contextful',hint:'Open Contextful tools, sources and graph',imgurl:'/contextful-inline.svg'}})
   options.status(options.file.can_write?'Ready · changes save to this file':'Ready · view access');emit()
  }
  if(name==='Folio_Inline_Ready')hello()
  if(name==='Folio_Bridge_Ready'&&values.session===session&&values.protocol===1){
   const offered=values.capabilities as Record<string,unknown>|undefined
   capabilities={protocol:1,engine:typeof values.engine==='string'?values.engine:'unknown',selection:offered?.selection===true,inline:offered?.inline===true,
    commands:offered?.commands===true?Object.keys(commands) as EditorCommand[]:[],nativeCharts:false,imagePaste:offered?.selection===true}
   if(!capabilities.selection)options.status('Assistance needs a compatible editor. File editing and saving remain available.')
   emit();inlineListeners.forEach(listener=>listener({type:'ready',values:{}}));return
  }
  if(name==='Clicked_Button'&&values.Id==='folio-contextful'){bridge('Folio_Inline_Close');send({MessageId:'Hide_Sidebar'});options.context()}
  if(name==='Action_Save_Resp')options.status(values.success===true?'Changes sent to Forge':'Save was not confirmed; keep the editor open and retry')
  // Folio bridge replies are session-bound; native Copy has no correlation ID.
  if(pending&&name===pending.name&&(name==='Action_Copy_Resp'||values.session===session&&values.id===pending.id)){
   const old=pending;pending=null;clearTimeout(old.timer)
   if(values.error)old.reject(Error(String(values.error)));else old.resolve(values)
   return
  }
  if(values.session!==session)return
  if(name==='Folio_Inline_Unavailable')options.status('Assistance needs a compatible editor. File editing and saving remain available.')
  const types:Record<string,InlineEvent['type']>={Folio_Inline_Begin:'begin',Folio_Inline_Request:'request',Folio_Inline_Cancel:'cancel'}
  if(name&&types[name]&&capabilities?.inline)inlineListeners.forEach(listener=>listener({type:types[name],values}))
 }
 window.addEventListener('message',receive)
 return {capabilities:()=>capabilities,loaded:()=>loaded,blocked:()=>poisoned,
  subscribe(listener){listeners.add(listener);return()=>{listeners.delete(listener)}},
  onInline(listener){inlineListeners.add(listener);return()=>{inlineListeners.delete(listener)}},
  async captureSelection(){
   ensureSelection();const id='selection-'+(++sequence)
   const response=await request('Folio_Tools_Selection',id,()=>bridge('Folio_Tools_Capture',{id}))
   if(!response.stamp||!['text','spreadsheet'].includes(String(response.kind)))throw Error('This editor cannot provide a supported selection yet.')
   const result=response as unknown as EditorSelection
   if(result.text==='__copy__')result.text=await copyText()
   return result
  },copyText,
  async focus(selection){ensureSelection();await request('Folio_Tools_Focused',selection.id,()=>bridge('Folio_Tools_Focus',{id:selection.id,stamp:selection.stamp}))},
  paste(data){send({MessageId:'Grab_Focus'});send({MessageId:'Action_Paste',Values:{Mimetype:typeof data==='string'?'text/plain;charset=utf-8':'image/png',Data:data}},typeof data!=='string')},
  async insertInline(id,text){
   ensureSelection()
   const reply=await request('Folio_Inline_Insert',id,()=>bridge('Folio_Inline_Result',{id,op:'apply',text}),5000)
   if(reply.text!==text)throw Error('The approved insertion no longer matches. Request a fresh suggestion.')
   send({MessageId:'Action_Paste',Values:{Mimetype:'text/plain;charset=utf-8',Data:text}})
   bridge('Folio_Inline_Result',{id,op:'inserted'})
  },
  async save(current){
   options.status('Saving to Forge…')
   try{const receipt=await saveToForge(options.file.file_id,send,()=>!stopped&&current());if(!stopped&&current())options.status('Last save confirmed in Forge');return receipt}
   catch(error){if(!stopped&&current())options.status('Save was not confirmed; keep the editor open and retry');throw error}
  },
  async command(command){
   if(!options.file.can_write||!capabilities?.commands.includes(command))throw Error('This editor command is unavailable.')
   const current=await office<ContentFile>('/files/'+options.file.file_id)
   if(stopped||!current.can_write)throw Error('Editing access is no longer available.')
   const anchorId='command-selection-'+(++sequence)
   const selection=await request('Folio_Tools_Selection',anchorId,()=>bridge('Folio_Tools_Capture',{id:anchorId}))
   if(!selection.stamp)throw Error('Place your cursor in the document and retry the command.')
   await request('Folio_Tools_Focused',anchorId,()=>bridge('Folio_Tools_Focus',{id:anchorId,stamp:selection.stamp}))
   // Narrow bridge checks the current cursor/permission and dispatches an
   // allow-listed native command. It does not claim durable save completion.
   const id='command-'+(++sequence)
   await request('Folio_Command_Resp',id,()=>bridge('Folio_Command',{id,command}))
  },
  configureInline:config=>bridge('Folio_Inline_Config',config),
  inlineResult:(id,values)=>bridge('Folio_Inline_Result',{...values,id}),
  openInline:values=>{if(capabilities?.inline)bridge('Folio_Inline_Open',values);else options.status('Assistance needs a compatible editor. File editing and saving remain available.')},
  closeInline:()=>bridge('Folio_Inline_Close'),hideSidebar:()=>send({MessageId:'Hide_Sidebar'}),
  dispose(){stopped=true;window.removeEventListener('message',receive);if(pending){clearTimeout(pending.timer);pending.reject(Error('The editor was closed.'));pending=null}listeners.clear();inlineListeners.clear()},
 }
}
export function createCollaboraEditor(options:Options){return createEditorSession(createCollaboraAdapter(options))}
